/**
 * useEmailDeliverabilityBackground
 *
 * Hook que mantiene en memoria la lista de identidades "en segundo plano"
 * (identidades que el usuario envio al background desde el wizard mientras
 * estaban verificando). Sincroniza con localStorage y con el WebSocket
 * de SendingIdentity para que el panel flotante refresque solo cuando
 * llega una transicion FSM (pending -> verifying -> verified/failed).
 *
 * Patron espejo de `useBackgroundUpload` de orbit pero adaptado a un
 * trabajo que corre en backend (no en el browser).
 */
import { useCallback, useEffect, useState } from 'react';

import { useSendingIdentityWebSocket } from './useSendingIdentityWebSocket';
import {
  addBackgroundIdentity as storageAdd,
  clearTerminalBackgroundIdentities as storageClearTerminal,
  listBackgroundIdentities as storageList,
  removeBackgroundIdentity as storageRemove,
  updateBackgroundIdentityStatus as storageUpdateStatus,
  BACKGROUND_CHANGED_EVENT,
  type BackgroundIdentity,
} from '../utils/emailDeliverabilityBackground';
import type {
  SendingIdentityDetail,
  SendingIdentityStatus,
} from '@/services/api/sendingIdentitiesService';

interface UseEmailDeliverabilityBackgroundReturn {
  items: BackgroundIdentity[];
  hasActive: boolean;
  activeCount: number;
  terminalCount: number;
  add: (identity: SendingIdentityDetail) => void;
  remove: (identityId: string) => void;
  clearTerminal: () => void;
}

export function useEmailDeliverabilityBackground(
  options: { wsEnabled?: boolean } = {},
): UseEmailDeliverabilityBackgroundReturn {
  const { wsEnabled = true } = options;
  const [items, setItems] = useState<BackgroundIdentity[]>(() => storageList());

  // Sincroniza con cambios en localStorage hechos desde otra tab del browser
  // (evento `storage` del browser solo cruza tabs).
  // Tambien escucha el evento custom `BACKGROUND_CHANGED_EVENT` que dispara
  // el helper cada vez que escribimos al storage — esto cubre el caso de
  // multiples instancias del hook montadas en la misma tab (wizard + indicator).
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'owly_email_deliverability_background') {
        setItems(storageList());
      }
    };
    const handleCustom = () => {
      setItems(storageList());
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener(BACKGROUND_CHANGED_EVENT, handleCustom);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(BACKGROUND_CHANGED_EVENT, handleCustom);
    };
  }, []);

  // WebSocket: cuando el backend emite transicion FSM, actualizamos la entry
  // en localStorage si corresponde. Solo activamos el WS si hay items, para
  // no abrir socket innecesariamente.
  useSendingIdentityWebSocket({
    enabled: wsEnabled && items.length > 0,
    onStatusChange: (data) => {
      // Solo nos interesa si la identity esta en nuestro background list
      const exists = items.some((i) => i.id === data.identity_id);
      if (!exists) return;
      const next = storageUpdateStatus(
        data.identity_id,
        data.status as SendingIdentityStatus,
      );
      setItems(next);
    },
  });

  const add = useCallback((identity: SendingIdentityDetail) => {
    const next = storageAdd(identity);
    setItems(next);
  }, []);

  const remove = useCallback((identityId: string) => {
    const next = storageRemove(identityId);
    setItems(next);
  }, []);

  const clearTerminal = useCallback(() => {
    const next = storageClearTerminal();
    setItems(next);
  }, []);

  const activeCount = items.filter(
    (i) => i.status !== 'verified' && i.status !== 'failed' && i.status !== 'disabled',
  ).length;
  const terminalCount = items.length - activeCount;

  return {
    items,
    hasActive: activeCount > 0,
    activeCount,
    terminalCount,
    add,
    remove,
    clearTerminal,
  };
}
